import React from 'react'
import { Platform } from 'react-native'

import { FormProvider } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import Button from 'app/components/base/Button'
import Checkbox from 'app/components/base/Checkbox'
import Divider from 'app/components/base/Divider'
import Link from 'app/components/base/Link'
import Stack from 'app/components/base/Stack'
import Text from 'app/components/base/Text'
import GeosuggestField from 'app/components/fields/GeosuggestField'
import PasswordField from 'app/components/fields/PasswordField'
import PhoneNumberField from 'app/components/fields/PhoneNumberField'
import SelectField from 'app/components/fields/SelectField'
import TextField from 'app/components/fields/TextField'
import countryOptions from 'app/data/mock/countryList'
import { IS_NATIVE_APP } from 'app/utils/constants/env.constants'
import {
  businessOptions,
  businessSegmentOptions
} from 'app/utils/constants/options.constants'
import { transformIntlOptions } from 'app/utils/helpers/transforms.helper'

import ReadTermLabel from './components/ReadTermLabel'
import WithTooltipContainer from './components/WithTooltipContainer'
import useSignUpBusinessForm from './hooks/useSignUpBusinessForm'

const SignUpBusinessForm = () => {
  const intl = useIntl()

  const {
    methods,
    setReadTerm,
    isReadTerm,
    handleSubmit,
    isOtherSegment,
    isSubmitting
  } = useSignUpBusinessForm()

  return (
    <FormProvider {...methods}>
      <Stack direction="column">
        <Text variant="h2" sx={{ textAlign: 'center', mb: 2 }}>
          <FormattedMessage defaultMessage="Create business account" />
        </Text>

        <Text
          variant="body1"
          sx={{ textAlign: 'center', color: 'text.third', mb: 2.5 }}
        >
          <FormattedMessage defaultMessage="Please enter your legally registered business information." />
        </Text>

        <Stack direction="column" spacing={3}>
          <Stack direction="column" spacing={2} sx={{ mb: 3 }}>
            <TextField
              name="business_name"
              label={intl.formatMessage({
                defaultMessage: 'Business Name'
              })}
            />

            <Stack spacing={2} direction="row">
              <TextField
                name="first_name"
                label={intl.formatMessage({
                  defaultMessage: 'First Name'
                })}
              />

              <TextField
                name="last_name"
                label={intl.formatMessage({
                  defaultMessage: 'Last Name'
                })}
              />
            </Stack>

            <PhoneNumberField
              name="phone_number"
              label={intl.formatMessage({
                defaultMessage: 'Business Phone Number'
              })}
            />

            <TextField
              name="email"
              label={intl.formatMessage({
                defaultMessage: 'Business Email'
              })}
            />

            <SelectField
              name="business_segment"
              optionSettings={{
                options: transformIntlOptions(
                  businessSegmentOptions,
                  intl.formatMessage
                )
              }}
              label={intl.formatMessage({
                defaultMessage: 'Business Segment'
              })}
            />

            {isOtherSegment && (
              <TextField
                name="custom_business_segment"
                label={intl.formatMessage({
                  defaultMessage: 'Comment'
                })}
                placeholder={intl.formatMessage({
                  defaultMessage: 'Type your own Business Segment here'
                })}
              />
            )}

            <SelectField
              name="business_location"
              optionSettings={{
                options: countryOptions,
                optionLabel: 'label',
                optionValue: 'code'
              }}
              label={intl.formatMessage({
                defaultMessage: 'Business Location'
              })}
              withFilter
            />

            <Divider
              style={{ marginBottom: 8, marginTop: IS_NATIVE_APP ? 16 : 32 }}
            />

            <Text
              variant="h5"
              sx={{ fontWeight: 500, mb: IS_NATIVE_APP ? 0 : 2.5 }}
            >
              <FormattedMessage defaultMessage="Business Address" />
            </Text>

            <GeosuggestField
              name="business_address_suggest"
              onChange={(value: GeosuggestValue) => {
                const { address, label } = value ?? {}
                const addressLabel = label ?? address?.label

                if (!address) {
                  methods.setValue('business_address_attributes', {
                    street_no: '',
                    street_name: '',
                    city: '',
                    postal_code: '',
                    country: '',
                    label: ''
                  })

                  methods.setValue('business_address_suggest', '')

                  return
                }

                methods.setValue('business_address_attributes', {
                  street_no: address?.street_number || '',
                  street_name: address?.street || '',
                  city: address?.city || '',
                  postal_code: address?.postal_code || '',
                  country: address?.full_country_name || '',
                  label: addressLabel || ''
                })

                methods.setValue('business_address_suggest', addressLabel, {
                  shouldDirty: true
                })

                methods.clearErrors([
                  'business_address_suggest',
                  'business_address_attributes'
                ])
              }}
            />

            <Divider
              style={{ marginBottom: 8, marginTop: IS_NATIVE_APP ? 16 : 32 }}
            />

            <WithTooltipContainer
              tooltipContent={intl.formatMessage({
                defaultMessage:
                  'A business number is a unique code assigned to incorporated businesses and varies by country. A business number can be known as CRN (Company Registration Number), ABN (Australian Business Number), SIREN/SIRET, Steuernummer, 商号 (Shōgō), among others."'
              })}
            >
              <TextField
                name="company_registration_number"
                label={intl.formatMessage({
                  defaultMessage: 'Business Number'
                })}
              />
            </WithTooltipContainer>

            <WithTooltipContainer
              tooltipContent={intl.formatMessage({
                defaultMessage:
                  'Your business type defines your industry and operations. Select the one that best suits your activities.'
              })}
            >
              <SelectField
                name="business_type"
                optionSettings={{
                  key: 'business_type',
                  options: transformIntlOptions(
                    businessOptions,
                    intl.formatMessage
                  )
                }}
                label={intl.formatMessage({
                  defaultMessage: 'Business Structure'
                })}
              />
            </WithTooltipContainer>

            <WithTooltipContainer
              tooltipContent={intl.formatMessage({
                defaultMessage:
                  "A Value Added Tax Identification Number (VAT ID) or Tax ID number is a unique identifier assigned to business's and is used for tax purposes. It’s essential for businesses engaged in taxable transactions."
              })}
            >
              <TextField
                name="tax_number"
                label={intl.formatMessage({
                  defaultMessage: 'VAT/TAX ID (Opt.).'
                })}
              />
            </WithTooltipContainer>

            <Stack direction="column" spacing={1}>
              <PasswordField
                name="password"
                label={intl.formatMessage({ defaultMessage: 'Password' })}
                helperText={
                  <FormattedMessage defaultMessage="Required min 8 symbols: Lowercase, Uppercase, Number" />
                }
              />

              <PasswordField
                name="password_confirmation"
                label={intl.formatMessage({
                  defaultMessage: 'Confirm Password'
                })}
              />

              <Stack spacing={1} direction="row">
                <Checkbox
                  checked={isReadTerm}
                  {...Platform.select({
                    web: {
                      onChange: (_, checked) => setReadTerm(checked)
                    },
                    native: {
                      onChange: (value) => setReadTerm(!!value)
                    }
                  })}
                  sx={{ p: 0, height: '18px' }}
                />

                <ReadTermLabel />
              </Stack>
            </Stack>
          </Stack>
        </Stack>

        <Button
          variant="contained"
          disabled={!isReadTerm}
          sx={{ mb: 4, mt: 3 }}
          onClick={handleSubmit}
          loading={isSubmitting}
        >
          <FormattedMessage defaultMessage="Sign up" />
        </Button>

        <Stack
          direction="row"
          sx={{ textAlign: 'center', justifyContent: 'center' }}
          spacing={0.5}
        >
          <Text variant="body2">
            <FormattedMessage defaultMessage="Already have an account?" />
          </Text>

          <Link
            href="/sign-in"
            variant="body2"
            sx={{ fontWeight: 500, color: 'text.primary' }}
          >
            <FormattedMessage defaultMessage="Sign in" />
          </Link>
        </Stack>
      </Stack>
    </FormProvider>
  )
}

export default SignUpBusinessForm
